import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/icons';
import { Notification } from '@/types';
import { api } from '@/api/client';

/** Category to badge tone. Beside the page that renders it, not on the server. */
const CATEGORY_TONE: Record<Notification['category'], string> = {
  ACTION_REQUIRED: 'danger',
  SECURITY: 'danger',
  ANNOUNCEMENT: 'info',
  SYSTEM: 'neutral',
};

/**
 * "2 hours ago" rather than a timestamp.
 *
 * An inbox is read by recency, and a reader comparing ISO strings in their head is doing
 * the page's job. It falls back to a date once the relative form stops helping — "43 days
 * ago" is less useful than the day it happened.
 */
function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const divisions: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
  ];

  let value = (then - Date.now()) / 1000;
  for (const [unit, size] of divisions) {
    if (Math.abs(value) < size) return formatter.format(Math.round(value), unit);
    value /= size;
  }
  return new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.notifications
      .list()
      .then((result) => {
        setNotifications(Array.isArray(result) ? result : []);
        setFailed(false);
      })
      // Kept distinct from "no notifications". An empty inbox and an unreachable server
      // look identical to a reader, and telling somebody they are all caught up when the
      // request failed is the quiet kind of wrong this codebase has had too much of.
      .catch(() => {
        setNotifications([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleMarkAllRead = async () => {
    // Optimistic, with the previous state held so a failure can restore it. Clearing an
    // inbox must feel instant, and the cost of being wrong is one badge coming back.
    const previous = notifications;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.notifications.markAllRead();
    } catch {
      setNotifications(previous);
    }
  };

  const open = (notification: Notification) => {
    if (!notification.read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
      );
      // Not awaited before navigating: a reader should not wait on bookkeeping, and a
      // failed mark leaves it unread — the safe direction to be wrong in.
      void api.notifications.markRead(notification.id).catch(() => undefined);
    }
    if (notification.link) navigate(notification.link);
  };

  if (loading) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.read).length;
  const visible = showUnreadOnly ? notifications.filter((n) => !n.read) : notifications;

  return (
    <div className="page-notifications">
      <div className="page-header">
        <h1>{t('notifications.title')}</h1>
        <p className="page-subtitle">{t('notifications.subtitle')}</p>
        <div className="notifications-actions">
          <button
            type="button"
            className="btn btn--tertiary"
            onClick={() => setShowUnreadOnly((value) => !value)}
            aria-pressed={showUnreadOnly}
          >
            {showUnreadOnly ? t('notifications.showAll') : t('notifications.showUnread')}
          </button>
          {unreadCount > 0 && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleMarkAllRead}
              aria-label={`${t('notifications.markAllRead')} (${unreadCount})`}
            >
              <Icon name="checkmark" size={16} aria-hidden="true" />
              {t('notifications.markAllRead')} ({unreadCount})
            </button>
          )}
        </div>
      </div>

      {failed ? (
        <div className="page-empty" role="alert">
          <Icon name="noNotifications" size={48} aria-hidden="true" />
          <h2>{t('notifications.loadFailed')}</h2>
          <p>{t('notifications.loadFailedDesc')}</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="page-empty" role="status">
          <Icon name="noNotifications" size={48} aria-hidden="true" />
          <h2>
            {showUnreadOnly ? t('notifications.allRead') : t('notifications.noNotifications')}
          </h2>
          <p>
            {showUnreadOnly
              ? t('notifications.allReadDesc')
              : t('notifications.noNotificationsDesc')}
          </p>
        </div>
      ) : (
        <div className="notifications-list" role="feed" aria-label={t('notifications.title')}>
          {visible.map((n) => (
            // A button rather than a div with a click handler: reachable by keyboard, a
            // role a screen reader understands, and Enter works with no extra code.
            <button
              type="button"
              key={n.id}
              className={`notification-row ${!n.read ? 'notification-row--unread' : ''}`}
              onClick={() => open(n)}
              aria-label={`${n.read ? '' : t('notifications.unread') + ': '}${n.title}`}
            >
              <div className="notification-rowDot" aria-hidden="true" />
              <div className="notification-rowContent">
                <div className="notification-rowHeader">
                  <span
                    className={`notification-category notification-category--${CATEGORY_TONE[n.category]}`}
                  >
                    {t(`notifications.category.${n.category}`)}
                  </span>
                  <time className="notification-rowTime" dateTime={n.createdAt}>
                    {relativeTime(n.createdAt, i18n.language)}
                  </time>
                </div>
                <p className="notification-rowMessage">{n.title}</p>
                <p className="notification-rowBody">{n.body}</p>
              </div>
              {n.link && <Icon name="external" size={14} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
